import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';
import { Search, TrendingUp, CreditCard, Landmark, PiggyBank, Repeat, Pencil } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v, dec = 0) =>
  v == null ? '—' : `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: dec })}`;
const monthLabel = (m) => {
  const [y, mo] = (m || '').split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString(undefined, { month: 'short' });
};
const prettyCat = (c) => (c || 'OTHER').replaceAll('_', ' ').toLowerCase()
  .replace(/\b\w/g, (ch) => ch.toUpperCase());
const dayLabel = (d) => {
  const dt = new Date(`${d}T12:00:00`);
  const today = new Date(); today.setHours(12, 0, 0, 0);
  const diff = Math.round((today - dt) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
};

const PIE_COLORS = ['#F59E0B', '#34d399', '#60a5fa', '#f87171', '#c084fc', '#fbbf24', '#2dd4bf', '#fb923c', '#a3e635', '#e879f9', '#94a3b8', '#f472b6'];
const RANGES = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: '3m', label: '3 months' },
  { key: '12m', label: 'Year' },
];

const tooltipStyle = {
  backgroundColor: '#141416', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8, fontSize: 12, color: '#e4e4e7',
};

const AccountIcon = ({ type }) => {
  if (type === 'credit') return <CreditCard className="w-4 h-4 text-red-400" />;
  if (type === 'loan') return <Landmark className="w-4 h-4 text-amber-400" />;
  return <PiggyBank className="w-4 h-4 text-emerald-400" />;
};

const Card = ({ children, testId, className = '' }) => (
  <div className={`glass-card p-4 sm:p-6 border border-white/10 ${className}`} data-testid={testId}>
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{children}</p>
);

export const WealthMonarch = () => {
  const [ov, setOv] = useState(null);
  const [range, setRange] = useState('this_month');
  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [editingCap, setEditingCap] = useState(null); // category being edited
  const [capValue, setCapValue] = useState('');

  const loadOverview = (r = range) => {
    axios.get(`${API}/wealth/overview`, { params: { range: r }, headers: getAuthHeaders() })
      .then((res) => setOv(res.data)).catch(() => {});
  };
  useEffect(() => { loadOverview(range); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/wealth/transactions`, { params: { q, limit: 40 }, headers: getAuthHeaders() })
        .then((r) => { setTxns(r.data.transactions || []); setTotal(r.data.total || 0); })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const saveCap = async (category) => {
    const val = parseFloat(capValue);
    try {
      const { data } = await axios.get(`${API}/wealth/budgets`, { headers: getAuthHeaders() });
      const caps = data.caps || {};
      if (val > 0) caps[category] = val; else delete caps[category];
      await axios.put(`${API}/wealth/budgets`, { caps }, { headers: getAuthHeaders() });
      toast.success(val > 0 ? `Cap set: ${prettyCat(category)} $${val.toLocaleString()}/mo` : 'Cap removed');
      setEditingCap(null); setCapValue('');
      loadOverview();
    } catch (e) { toast.error('Could not save the cap'); }
  };

  const nwSeries = useMemo(() =>
    (ov?.net_worth_series || []).map((s) => ({
      day: (s.day || '').slice(5), net_worth: s.net_worth, cash: s.cash,
    })), [ov]);
  const flow = useMemo(() =>
    (ov?.cashflow_months || []).map((m) => ({ ...m, label: monthLabel(m.month) })), [ov]);
  const pieData = useMemo(() =>
    (ov?.categories_this_month || []).filter((c) => c.amount > 0)
      .map((c) => ({ name: prettyCat(c.category), value: c.amount })), [ov]);
  const txnsByDay = useMemo(() => {
    const groups = {};
    txns.forEach((t) => {
      const d = t.date || 'unknown';
      (groups[d] = groups[d] || []).push(t);
    });
    return Object.entries(groups).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [txns]);

  if (!ov) return null;
  const s = ov.this_month_summary || {};
  const bt = ov.budget_totals || {};

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="wealth-monarch">

      {/* ============ THIS MONTH — dead simple ============ */}
      <Card testId="month-summary-card">
        <CardTitle>This month, in plain terms</CardTitle>
        <div className="grid grid-cols-3 gap-2 sm:gap-6 mt-3">
          <div>
            <p className="text-[11px] sm:text-xs text-zinc-500">Came in</p>
            <p className="text-lg sm:text-3xl font-bold font-mono text-emerald-400" data-testid="month-came-in">{money(s.came_in)}</p>
          </div>
          <div>
            <p className="text-[11px] sm:text-xs text-zinc-500">Went out</p>
            <p className="text-lg sm:text-3xl font-bold font-mono text-red-400" data-testid="month-went-out">{money(s.went_out)}</p>
          </div>
          <div>
            <p className="text-[11px] sm:text-xs text-zinc-500">Left over</p>
            <p className={`text-lg sm:text-3xl font-bold font-mono ${(s.left_over || 0) >= 0 ? 'text-zinc-100' : 'text-red-400'}`} data-testid="month-left-over">{money(s.left_over)}</p>
          </div>
        </div>
        <p className="text-[11px] text-zinc-600 mt-2">{s.days_left} days left in the month · {s.note}</p>
        {s.pace_note && s.went_out > 0 && (
          <p className="text-[12px] text-amber-300/90 mt-1.5 font-medium" data-testid="month-pace-note">{s.pace_note}</p>
        )}
      </Card>

      {/* ============ BUDGETS — left to spend ============ */}
      <Card testId="budgets-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>Spending caps</CardTitle>
          {bt.cap > 0 && (
            <span className={`text-sm font-mono font-bold ${bt.left >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="budget-left-banner">
              {bt.left >= 0 ? `${money(bt.left)} left to spend` : `${money(Math.abs(bt.left))} over your caps`}
            </span>
          )}
        </div>
        <div className="mt-3 space-y-3">
          {(ov.categories_this_month || []).slice(0, 8).map((c) => {
            const pct = c.cap ? Math.min((c.amount / c.cap) * 100, 100) : 0;
            const over = c.cap && c.amount > c.cap;
            return (
              <div key={c.category} data-testid="budget-row">
                <div className="flex justify-between items-center text-[12px] mb-1 gap-2">
                  <span className="text-zinc-300 truncate">{prettyCat(c.category)}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-zinc-200">
                      {money(c.amount)}{c.cap ? <span className={over ? 'text-red-400' : 'text-zinc-500'}> of {money(c.cap)}</span> : <span className="text-zinc-600"> spent</span>}
                    </span>
                    {editingCap === c.category ? (
                      <span className="flex items-center gap-1">
                        <input autoFocus value={capValue} onChange={(e) => setCapValue(e.target.value)} type="number" placeholder="$/mo"
                          onKeyDown={(e) => e.key === 'Enter' && saveCap(c.category)}
                          className="w-20 bg-black/40 border border-white/15 rounded px-1.5 py-0.5 text-[12px] text-white" data-testid="cap-input" />
                        <button onClick={() => saveCap(c.category)} className="text-emerald-400 text-[11px] font-bold" data-testid="cap-save-btn">Save</button>
                      </span>
                    ) : (
                      <button onClick={() => { setEditingCap(c.category); setCapValue(c.cap || ''); }}
                        className="text-zinc-600 hover:text-amber-300 transition-colors" title="Set a monthly cap" data-testid="cap-edit-btn">
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className={`h-full ${over ? 'bg-red-400' : c.cap ? 'bg-emerald-400/80' : 'bg-primary/60'}`}
                    style={{ width: `${c.cap ? pct : Math.min((c.amount / Math.max(1, ...(ov.categories_this_month || []).map((x) => x.amount))) * 100, 100)}%` }} />
                </div>
              </div>
            );
          })}
          {(ov.categories_this_month || []).length === 0 && (
            <p className="text-sm text-zinc-500">No spending yet this month.</p>
          )}
        </div>
        <p className="text-[11px] text-zinc-600 mt-3">Tap the pencil to set a monthly cap — LEE warns you before you blow past it.</p>
      </Card>

      {/* ============ SPENDING DONUT ============ */}
      <Card testId="spending-donut-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>Where the money went</CardTitle>
          <div className="flex gap-1.5 flex-wrap">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setRange(r.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${range === r.key ? 'bg-primary text-black' : 'bg-white/[0.05] text-zinc-400 hover:bg-white/10'}`}
                data-testid={`range-${r.key}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {pieData.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center mt-2">
            <div className="relative h-56" style={{ minHeight: 224 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="90%" paddingAngle={2} stroke="none">
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl sm:text-2xl font-bold font-mono text-zinc-100" data-testid="donut-total">{money(ov.range_summary?.spending)}</span>
                <span className="text-[11px] text-zinc-500">{ov.range_summary?.transactions} transactions</span>
              </div>
            </div>
            <div className="space-y-1.5">
              {pieData.slice(0, 8).map((p, i) => (
                <div key={p.name} className="flex items-center justify-between text-[13px]" data-testid="donut-legend-row">
                  <span className="flex items-center gap-2 text-zinc-300 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="truncate">{p.name}</span>
                  </span>
                  <span className="font-mono text-zinc-200 shrink-0">{money(p.value)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-zinc-500 mt-3">No spending in this period.</p>
        )}
      </Card>

      {/* ============ NET WORTH + ACCOUNTS ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <Card testId="networth-trend-card" className="lg:col-span-3">
          <CardTitle><span className="flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5 text-primary" /> Net worth trend</span></CardTitle>
          {nwSeries.length >= 2 ? (
            <div className="h-52 mt-3" style={{ minHeight: 208 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <AreaChart data={nwSeries} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                  <defs>
                    <linearGradient id="nwFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="day" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} domain={['auto', 'auto']} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money(v, 2), n === 'net_worth' ? 'Net worth' : 'Cash']} />
                  <Area type="monotone" dataKey="net_worth" stroke="#F59E0B" strokeWidth={2} fill="url(#nwFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-zinc-500 mt-4">
              Daily snapshots are building your trend line — {nwSeries.length} day{nwSeries.length === 1 ? '' : 's'} captured so far. Check back tomorrow.
            </p>
          )}
        </Card>

        <Card testId="accounts-card" className="lg:col-span-2">
          <CardTitle>Accounts</CardTitle>
          <div className="mt-3 space-y-2.5 max-h-52 overflow-y-auto pr-1">
            {(ov.accounts || []).map((a) => (
              <div key={a.account_id} className="flex items-center justify-between gap-2" data-testid="account-row">
                <div className="flex items-center gap-2.5 min-w-0">
                  <AccountIcon type={a.account_type} />
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-200 truncate">{a.account_name} <span className="text-zinc-600">··{a.mask}</span></p>
                    <p className="text-[11px] text-zinc-500">{a.institution_name} · {a.account_subtype}</p>
                  </div>
                </div>
                <span className={`font-mono text-sm shrink-0 ${a.account_type === 'credit' || a.account_type === 'loan' ? 'text-red-300' : 'text-zinc-100'}`}>
                  {money(a.balance_current, 2)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ============ CASHFLOW BARS ============ */}
      <Card testId="cashflow-chart-card">
        <CardTitle>Cash flow · last {flow.length} months (transfers excluded)</CardTitle>
        <div className="h-56 mt-3" style={{ minHeight: 224 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={flow} margin={{ top: 5, right: 5, bottom: 0, left: 5 }} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money(v, 0), n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="income" name="Income" fill="#34d399" radius={[3, 3, 0, 0]} />
              <Bar dataKey="spending" name="Spending" fill="#f87171" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* ============ RECURRING ============ */}
      <Card testId="recurring-card">
        <CardTitle><span className="flex items-center gap-2"><Repeat className="w-3.5 h-3.5 text-amber-400" /> Recurring charges detected</span></CardTitle>
        <div className="mt-3 space-y-2.5">
          {(ov.recurring || []).length === 0 && <p className="text-sm text-zinc-500">No recurring charges detected yet.</p>}
          {(ov.recurring || []).slice(0, 6).map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-2" data-testid="recurring-row">
              <div className="min-w-0">
                <p className="text-sm text-zinc-200 truncate">{r.merchant}</p>
                <p className="text-[11px] text-zinc-500">{r.occurrences}x seen · last {r.last_charge}</p>
              </div>
              <span className="font-mono text-sm text-amber-300 shrink-0">{money(r.monthly_est)}/mo</span>
            </div>
          ))}
        </div>
      </Card>

      {/* ============ TRANSACTIONS — grouped by day ============ */}
      <Card testId="transactions-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle>Transactions <span className="text-zinc-600 normal-case">({total})</span></CardTitle>
          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merchant, category…"
              className="w-full bg-black/40 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-base sm:text-sm text-white placeholder-zinc-600"
              data-testid="txn-search-input" />
          </div>
        </div>
        <div className="mt-2">
          {txnsByDay.length === 0 && <p className="text-sm text-zinc-500 py-3">No transactions match.</p>}
          {txnsByDay.map(([day, list]) => {
            const dayNet = list.reduce((acc, t) => acc - (t.amount || 0), 0);
            return (
              <div key={day} data-testid="txn-day-group">
                <div className="flex items-center justify-between mt-4 mb-1">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{dayLabel(day)}</p>
                  <span className={`text-[11px] font-mono ${dayNet >= 0 ? 'text-emerald-500' : 'text-zinc-500'}`}>{dayNet >= 0 ? '+' : '−'}${Math.abs(dayNet).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
                <div className="divide-y divide-white/[0.04]">
                  {list.map((t) => (
                    <div key={t.transaction_id} className="flex items-center justify-between gap-3 py-2" data-testid="txn-row">
                      <div className="min-w-0 flex items-center gap-2">
                        {t.pending && <span className="shrink-0 w-4 h-4 rounded-full bg-zinc-700 text-[9px] font-bold text-zinc-300 flex items-center justify-center" title="Pending">P</span>}
                        <div className="min-w-0">
                          <p className="text-sm text-zinc-200 truncate">{t.merchant_name || t.description}</p>
                          <p className="text-[11px] text-zinc-500">{prettyCat(t.category)}</p>
                        </div>
                      </div>
                      <span className={`font-mono text-sm shrink-0 ${t.amount < 0 ? 'text-emerald-400' : 'text-zinc-200'}`}>
                        {t.amount < 0 ? '+' : '−'}${Math.abs(t.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};
